import { createContext, useContext, useEffect, useState, useMemo } from "react";
import { useRouter } from "next/router";
import { CognitoHostedUIIdentityProvider, Auth } from "@aws-amplify/auth";
import { Amplify } from "aws-amplify";
import Base64 from "crypto-js/enc-base64";
import hmacSHA512 from "crypto-js/hmac-sha512";
import md5 from "md5";
import moment from "moment";
import awsweb from "../config/aws-exports";
import { actionType, eventName, pageName, section } from "../constants/events/eventName";
import { DEFAULT_COUNTRY_CODE } from "../constants/variables";
import { getTestEnvEntryPassword } from "../dataLayer/cms";
import { fetchCustomerData } from "../dataLayer/crm";
import { moEngagePush, trackUserActivity } from "../services/trackUserActivity";

Amplify.configure(awsweb);

const currentConfig = Auth.configure();

const AuthContext = createContext<any>({});

export const useAuth = () => useContext(AuthContext);

export function AuthContextProvider({ children }) {
  const [confirmationResult, setConfirmationResult] = useState<any>(null);
  const [user, setUser] = useState<any>(null);
  const [userConfirmed, setUserConfirmed] = useState({
    user: "",
    status: false,
    otp: ""
  });
  const [isLogin, setIsLogin] = useState(false);
  const [logoutAttempt, setLogoutAttempt] = useState(0);
  const [currentTypeIndex, setCurrentTypeIndex] = useState(1);

  const userTypeGroup = [
    {
      isGuest: true,
      isLoggedIn: false,
      isClubAkasaMember: false,
      isYetToBeCustomer: true,
      hasActiveBooking: false,
      isEligibleForGoldCoinOffer: false
    }, // above is guest user
    {
      isGuest: false,
      isLoggedIn: true,
      isClubAkasaMember: false,
      isYetToBeCustomer: true,
      hasActiveBooking: true,
      isEligibleForGoldCoinOffer: false
    }, // logined user and active booking
    {
      isGuest: false,
      isLoggedIn: false,
      isClubAkasaMember: true,
      isYetToBeCustomer: false,
      hasActiveBooking: true,
      isEligibleForGoldCoinOffer: true
    }
  ];
  const [userData, setUserData] = useState(userTypeGroup[currentTypeIndex]);
  // const [loading, setLoading] = useState(true)
  // const [userObject, setUserObject] = useState({});
  let userObject = {};
  const router = useRouter();

  useEffect(() => {
    setUser(null);
    setUserData(userTypeGroup[0]); // first index is for login user
    setCurrentTypeIndex(0);
    // setLoading(false)
    currentAuthUser();
  }, []);

  const currentAuthUser = (eventFire = false) => {
    Auth.currentAuthenticatedUser().then(async (currentUser) => {
      await Auth.currentSession().then((data) => {
        sessionStorage.setItem("auth-access-token", data.getIdToken().getJwtToken());
      });
      setUser(currentUser);
      // if(eventFire){
      //   trackUserActivity({
      //     page: pageName.LOGIN_FLOW,
      //     section: section.LOGIN_FLOW,
      //     actionType: actionType.CLICK,
      //     value: "login",
      //     eventName: eventName.LOGIN_SUCCESS,
      //     details: {
      //       user: currentUser.attributes,
      //     },
      //   });
      // }
      setUserNameData(currentUser, eventFire);
      setUserData(userTypeGroup[1]); // first index is for login user
      setCurrentTypeIndex(1);
    });
  };

  async function setUserNameData(cognitoUser, eventFire = false) {
    const cognitoObj = JSON.parse(JSON.stringify(cognitoUser));
    if (
      typeof cognitoObj !== "undefined" &&
      typeof cognitoObj?.attributes !== "undefined" &&
      typeof cognitoObj?.attributes["custom:crmId"] !== "undefined"
    ) {
      const response = await fetchCustomerData(cognitoObj?.attributes["custom:crmId"]);
      if (response) {
        if (eventFire) {
          window.Moengage.add_first_name(response?.cusFName)
          window.Moengage.add_last_name(response?.cusLName);
          window.Moengage.add_email(response?.cusEmail);
          window.Moengage.add_mobile(response?.cusMobile);
          window.Moengage.add_unique_user_id(response?.cusLoyaltyId); 

          trackUserActivity({
            page: pageName.LOGIN_FLOW,
            section: section.LOGIN_FLOW,
            actionType: actionType.CLICK,
            value: "login",
            eventName: eventName.LOGIN_SUCCESS,
            details: {
              user: response
            }
          });
        }

        setUser(response);
      }
    }
  }

  // const signup = (email: string, password: string) => {
  //   return createUserWithEmailAndPassword(auth, email, password)

  // }
  const confirmSignUp = (otp: string) => {
    try {
      Auth.confirmSignUp(userConfirmed.user, otp).then((data) => {
        if (data) {
          Auth.currentAuthenticatedUser().then((currentUser) => {
            setUser(currentUser);

            window.Moengage.add_first_name(user?.cusFName)
            window.Moengage.add_last_name(user?.cusLName);
            window.Moengage.add_email(user?.cusEmail);
            window.Moengage.add_mobile(user?.cusMobile);
            window.Moengage.add_unique_user_id(user?.cusLoyaltyId); 

            moEngagePush("sign up",{
              firstName : user?.cusFName,
              lastName : user?.cusLName,
              email: user?.cusEmail,
              mobileNumber : user?.cusMobile,
              // loyaltyId : user?.cusLoyaltyId
            })
          });
        }
      });
    } catch (error) {
      console.warn("error confirming sign up", error);
    }
  };

  const signup = async (userData: any) => {
    try {
      let error = false;
      let err_resp;

      const emailSignUp = {
        username: userData.emailID,
        password: userData.password,
        attributes: {
          name: `${userData.firstName} ${userData.lastName}`,
          email: userData.emailID,
          "custom:crm_username": userData.emailID,
          given_name: userData.firstName,
          family_name: userData.lastName,
          birthdate: moment(userData.dob).format("DD/MM/YYYY"),
          gender: userData.gender,
          "custom:tnc_accepted": "true",
          phone_number: DEFAULT_COUNTRY_CODE + userData.mobile
        }
      };

      await Auth.signUp(emailSignUp)
        .then((user) => {

          window.Moengage.add_first_name(user?.cusFName)
          window.Moengage.add_last_name(user?.cusLName);
          window.Moengage.add_email(user?.cusEmail);
          window.Moengage.add_mobile(user?.cusMobile);
          window.Moengage.add_unique_user_id(user?.cusLoyaltyId); 

          moEngagePush("sign up",{
            firstName : user?.cusFName,
            lastName : user?.cusLName,
            email: user?.cusEmail,
            mobileNumber : user?.cusMobile,
            loyaltyId : user?.cusLoyaltyId
          })
        })
        .catch((err) => {
          error = true;
          err_resp = err;
        });

      if (error) {
        return err_resp;
      }

      await login(userData.emailID, userData.password).then((data) => {
        window.Moengage.add_first_name(user?.cusFName)
        window.Moengage.add_last_name(user?.cusLName);
        window.Moengage.add_email(user?.cusEmail);
        window.Moengage.add_mobile(user?.cusMobile);
        window.Moengage.add_unique_user_id(user?.cusLoyaltyId); 


        moEngagePush("sign up",{
          firstName : user?.cusFName,
          lastName : user?.cusLName,
          email: user?.cusEmail,
          mobileNumber : user?.cusMobile,
          loyaltyId : user?.cusLoyaltyId
        })
        return true
      });

      return true;
    } catch (error) {
      console.warn("error signing up:", error);
      return error;
    }
  };



  const login = async (email: any, password: string) => {
    try {
      let isSuccess = false;
      if (!isNaN(email)) {
        email = DEFAULT_COUNTRY_CODE + email;
      }
      await Auth.signIn(email, password).then((respData) => {
        if (respData) {
          isSuccess = true;
          return true;
        }
      });

      window.Moengage.add_first_name(user?.cusFName)
      window.Moengage.add_last_name(user?.cusLName);
      window.Moengage.add_email(user?.cusEmail);
      window.Moengage.add_mobile(user?.cusMobile);
      window.Moengage.add_unique_user_id(user?.cusLoyaltyId); 
      window.Moengage.add_unique_user_id(user?.cusLoyaltyId)


      moEngagePush(eventName.SIGNUP_SUCCESS,{
        firstName : user?.cusFName,
        lastName : user?.cusLName,
        email: user?.cusEmail,
        mobileNumber : user?.cusMobile,
        loyaltyId : user?.cusLoyaltyId,
        emails:email
      })

 
      return isSuccess;
    } catch (error) {
      console.warn("error signing in", error);
      return false;
    }
  };

  const staticlogin = async (mobile: string, password: string) => {
    const hardPass = `${mobile.substring(0, 4)}@akasa`;
    let resp = "error";
    let passwordFromCMS: any = await getTestEnvEntryPassword();
    passwordFromCMS = passwordFromCMS?.data?.story?.content?.testEnvEntryPassword;

    if (password == passwordFromCMS) {
      // combination is good. Log them in.
      // this token can be anything. You can use random.org to generate a random string;
      const token = "123456abcdsadasdef";
      sessionStorage.setItem("testEnvEntryToken", token);
      // alert("logging in, please wait..");

      resp = "success";

      window.Moengage.add_first_name(user?.cusFName)
      window.Moengage.add_last_name(user?.cusLName);
      window.Moengage.add_email(user?.cusEmail);
      window.Moengage.add_mobile(user?.cusMobile);
      window.Moengage.add_unique_user_id(user?.cusLoyaltyId); 


      moEngagePush(eventName.SIGNUP_SUCCESS,{
        firstName : user?.cusFName,
        lastName : user?.cusLName,
        email: user?.cusEmail,
        mobileNumber : user?.cusMobile,
        loyaltyId : user?.cusLoyaltyId
      })
      // go to www.website.com/todo
    } else {
      // bad combination
      resp = "error";
      // alert("wrong email or password combination");
      // return false;
    }

    return resp;
  };

  const logout = async () => {
    setUser(null);

    window.Moengage.add_first_name(user?.cusFName)
    window.Moengage.add_last_name(user?.cusLName);
    window.Moengage.add_email(user?.cusEmail);
    window.Moengage.add_mobile(user?.cusMobile);
    window.Moengage.add_unique_user_id(user?.cusLoyaltyId); 
    window.Moengage.destroy_session();


    moEngagePush( eventName.LOGOUT_SUCCESS,{
      firstName : user?.cusFName,
      lastName : user?.cusLName,
      email: user?.cusEmail,
      mobileNumber : user?.cusMobile,
      loyaltyId : user?.cusLoyaltyId
    })

   
  };

  const sendOtp = async (number) => {
    try {
      const respData = await Auth.signIn(number, "");
      if (respData) {
        userObject = respData;
      }
      return respData;
    } catch (error) {
      signUpWIthMobileOnly(number);
      console.warn("error signing in", error);
      //   return false;
    }
  };

  const verifyOtp = async (otp) => {
    otp = Base64.stringify(hmacSHA512(otp, process.env.NEXT_PUBLIC_ENC_KEY));

    try {
      let isSuccess = false;
      await Auth.sendCustomChallengeAnswer(userObject, otp).then((response) => {
        if (response.signInUserSession) {
          isSuccess = true;
          return true;
        }
        isSuccess = false;
        return false;
      });
      return isSuccess;
    } catch (error) {
      console.warn("error signing in", error);
      return false;
    }
  };

  // Send confirmation code to user's email
  const forgetPassword = async (username) => {
    Auth.forgotPassword(username)
      .then((data) => true)
      .catch((err) => {
        console.warn(err);
        return false;
      });
  };

  // Collect confirmation code and new password, then
  const forgotPasswordSubmit = async (username, code, new_password) => {
    try {
      let isSuccess = false;
      await Auth.forgotPasswordSubmit(username, code, new_password).then((data) => {
        isSuccess = true;
      });
      return isSuccess;
    } catch (error) {
      console.warn("error signing in", error);
      return false;
    }
  };

  const loginWithGoogle = () => {
    Auth.federatedSignIn({
      provider: CognitoHostedUIIdentityProvider.Google
    }).then((data) => {


      window.Moengage.add_first_name(user?.cusFName)
      window.Moengage.add_last_name(user?.cusLName);
      window.Moengage.add_email(user?.cusEmail);
      window.Moengage.add_mobile(user?.cusMobile);
      window.Moengage.add_unique_user_id(user?.cusLoyaltyId); 

      moEngagePush(eventName.SIGNUP_SUCCESS,{
        firstName : user?.cusFName,
        lastName : user?.cusLName,
        email: user?.cusEmail,
        mobileNumber : user?.cusMobile,
        loyaltyId : user?.cusLoyaltyId,
        emails:email,
        source:"login with google"
      })

    });
  };
  const loginWithFacebook = () => {
    Auth.federatedSignIn({
      provider: CognitoHostedUIIdentityProvider.Facebook
    }).then((data) => {

      window.Moengage.add_first_name(user?.cusFName)
      window.Moengage.add_last_name(user?.cusLName);
      window.Moengage.add_email(user?.cusEmail);
      window.Moengage.add_mobile(user?.cusMobile);
      window.Moengage.add_unique_user_id(user?.cusLoyaltyId); 

      moEngagePush(eventName.SIGNUP_SUCCESS,{
        firstName : user?.cusFName,
        lastName : user?.cusLName,
        email: user?.cusEmail,
        mobileNumber : user?.cusMobile,
        loyaltyId : user?.cusLoyaltyId,
        emails:email,
        source:"login with facebook"
      })
    });
  };
  const loginWithApple = () => {
    Auth.federatedSignIn({
      provider: CognitoHostedUIIdentityProvider.Apple
    }).then((data) => {
      window.Moengage.add_unique_user_id(user?.cusLoyaltyId);

      moEngagePush(eventName.SIGNUP_SUCCESS,{
        firstName : user?.cusFName,
        lastName : user?.cusLName,
        email: user?.cusEmail,
        mobileNumber : user?.cusMobile,
        loyaltyId : user?.cusLoyaltyId,
        emails:email,
        source:"login with apple"
      })
    });
  };

  const signOut = async () => {
    try {
      let isSuccess = false;
      window.Moengage.add_first_name(user?.cusFName)
      window.Moengage.add_last_name(user?.cusLName);
      window.Moengage.add_email(user?.cusEmail);
      window.Moengage.add_mobile(user?.cusMobile);
      window.Moengage.add_unique_user_id(user?.cusLoyaltyId); 
      window.Moengage.destroy_session();
      await Auth.signOut().then((resp) => {
        setUser(null);
        setUserData(userTypeGroup[0]); // first index is for login user
        setCurrentTypeIndex(0);
        sessionStorage.removeItem("auth-access-token");
        setLogoutAttempt(0);
        isSuccess = true;
      });
      moEngagePush(eventName.LOGOUT_SUCCESS, {
        firstName : user?.cusFName,
        lastName : user?.cusLName,
        email: user?.cusEmail,
        mobileNumber : user?.cusMobile,
        loyaltyId : user?.cusLoyaltyId
      })
      return isSuccess;
    } catch (error) {
      console.warn("error signing out: ", error);
    }
  };

  const signUpWIthMobileOnly = async (number) => {
    // Dummy password needed for signup with mobile only
    const passwd = `${number}@Pass`;
    try {
      const respData = await Auth.signUp({
        username: number,
        password: passwd,
        attributes: {
          phone_number: number,
          "custom:tnc_accepted": "true"
        }
      });
      if (respData) {
        sendOtp(number);
        window.Moengage.add_first_name(user?.cusFName)
        window.Moengage.add_last_name(user?.cusLName);
        window.Moengage.add_email(user?.cusEmail);
        window.Moengage.add_mobile(user?.cusMobile);
        window.Moengage.add_unique_user_id(user?.cusLoyaltyId); 

        moEngagePush("SignUpWithMobileOnly",{
          number,
          "custom:tnc_accepted": "true"
        })
      }
      return true;
    } catch (error) {
      console.warn("error signing in", error);
      return false;
    }
  };

  const userExist = async (username: string) =>
    // 123 is dummy pass just to check username existance
    Auth.signIn(username, "123")
      .then((res) => false)
      .catch((error) => {
        const { code } = error;
        switch (code) {
          case "UserNotFoundException":
            return false;
          case "NotAuthorizedException":
            return true;
          case "PasswordResetRequiredException":
            return false;
          default:
            return false;
        }
      });
  async function deleteUser() {
    try {
      const result = await Auth.deleteUser();
      console.warn(result);
    } catch (error) {
      console.warn("Error deleting user", error);
    }
  }

  const changePassword = async (oldPassword: string, newPassword: string, successCb: any) => {
    Auth.currentAuthenticatedUser()
      .then((user) => Auth.changePassword(user, oldPassword, newPassword))
      .then((res) => {
        successCb(res);
        return res;
      })
      .catch((err) => {
        console.warn("Error", err.message);
        successCb(err);
        return err;
      });
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        userConfirmed,
        setUser,
        login,
        signup,
        signOut,
        logout,
        sendOtp,
        verifyOtp,
        loginWithGoogle,
        loginWithFacebook,
        loginWithApple,
        staticlogin,
        confirmSignUp,
        currentAuthUser,
        forgetPassword,
        forgotPasswordSubmit,
        userExist,
        deleteUser,
        userData,
        changePassword
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}